import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useNotification } from '../../../contexts/NotificationContext';
import customerService from '../../../utils/customerService';

const StageConfigModal = ({ isOpen, onClose, onSave }) => {
  const [stages, setStages] = useState([]);
  const [editingStage, setEditingStage] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { currentOrganization } = useOrganization();
  const { addNotification } = useNotification();

  // Available color options (hex values for database storage)
  const colorOptions = [
    { value: '#3B82F6', label: 'Blue', preview: 'bg-blue-100' },
    { value: '#10B981', label: 'Green', preview: 'bg-green-100' },
    { value: '#F59E0B', label: 'Yellow', preview: 'bg-yellow-100' },
    { value: '#EF4444', label: 'Red', preview: 'bg-red-100' },
    { value: '#8B5CF6', label: 'Purple', preview: 'bg-purple-100' },
    { value: '#F97316', label: 'Orange', preview: 'bg-orange-100' },
    { value: '#EC4899', label: 'Pink', preview: 'bg-pink-100' },
    { value: '#6366F1', label: 'Indigo', preview: 'bg-indigo-100' },
    { value: '#14B8A6', label: 'Teal', preview: 'bg-teal-100' },
    { value: '#6B7280', label: 'Gray', preview: 'bg-gray-100' }
  ];

  // Available icon options
  const iconOptions = [
    'UserPlus', 'Phone', 'FileText', 'Calendar', 'CheckCircle', 'Mail', 'Clock', 
    'DollarSign', 'AlertCircle', 'Users', 'Briefcase', 'Star', 'Target', 'TrendingUp',
    'Trophy', 'XCircle', 'MessageSquare', 'Circle'
  ];

  // Load stages from database
  useEffect(() => {
    const loadStages = async () => {
      if (!isOpen || !currentOrganization?.id) return;
      
      try {
        setLoading(true);
        const stagesData = await customerService.getPipelineStages(currentOrganization.id);
        setStages(stagesData.sort((a, b) => a.position - b.position));
      } catch (error) {
        console.error('Error loading pipeline stages:', error);
        // Set default stages if loading fails
        setStages([
          { id: 'temp-1', name: 'Lead', color: '#3B82F6', icon: 'UserPlus', position: 1, is_active: true, triggers_review: false },
          { id: 'temp-2', name: 'Contacted', color: '#F59E0B', icon: 'Phone', position: 2, is_active: true, triggers_review: false },
          { id: 'temp-3', name: 'Qualified', color: '#8B5CF6', icon: 'CheckCircle', position: 3, is_active: true, triggers_review: false },
          { id: 'temp-4', name: 'Proposal Sent', color: '#F97316', icon: 'FileText', position: 4, is_active: true, triggers_review: false },
          { id: 'temp-5', name: 'Negotiation', color: '#EF4444', icon: 'MessageSquare', position: 5, is_active: true, triggers_review: false },
          { id: 'temp-6', name: 'Won', color: '#10B981', icon: 'Trophy', position: 6, is_active: true, triggers_review: true },
          { id: 'temp-7', name: 'Lost', color: '#6B7280', icon: 'XCircle', position: 7, is_active: true, triggers_review: false }
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadStages();
  }, [isOpen, currentOrganization?.id]);

  const handleAddStage = () => {
    const newStage = {
      id: `temp-new-${Date.now()}`,
      name: '',
      color: '#6B7280',
      icon: 'Circle',
      position: stages.length + 1,
      is_active: true,
      triggers_review: false
    };
    setEditingStage(newStage);
    setIsAddingNew(true);
  };

  const handleEditStage = (stage) => {
    setEditingStage({ ...stage });
    setIsAddingNew(false);
  };

  const handleSaveStage = () => {
    if (!editingStage.name.trim()) {
      addNotification('Stage name is required', 'error');
      return;
    }

    // Check for duplicate stage names
    const isDuplicate = stages.some(stage => 
      stage.id !== editingStage.id && 
      stage.name.toLowerCase().trim() === editingStage.name.toLowerCase().trim()
    );

    if (isDuplicate) {
      addNotification('A stage with this name already exists', 'error');
      return;
    }

    const updatedStage = {
      ...editingStage,
      name: editingStage.name.trim()
    };

    if (isAddingNew) {
      setStages(prev => [...prev, updatedStage]);
      addNotification('Stage added successfully', 'success');
    } else {
      setStages(prev => prev.map(stage => 
        stage.id === editingStage.id ? updatedStage : stage
      ));
      addNotification('Stage updated successfully', 'success');
    }

    setEditingStage(null);
    setIsAddingNew(false);
  };

  const handleDeleteStage = (stageToDelete) => {
    if (window.confirm('Are you sure you want to delete this stage? This action cannot be undone.')) {
      setStages(prev => prev.filter(stage => stage.id !== stageToDelete.id));
      addNotification(`Stage "${stageToDelete.name}" deleted successfully`, 'success');
    }
  };

  const handleMoveStage = (stageId, direction) => {
    const currentIndex = stages.findIndex(stage => stage.id === stageId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= stages.length) return;

    const newStages = [...stages];
    const [movedStage] = newStages.splice(currentIndex, 1);
    newStages.splice(newIndex, 0, movedStage);
    
    // Update positions
    const updatedStages = newStages.map((stage, index) => ({
      ...stage,
      position: index + 1
    }));
    
    setStages(updatedStages);
    addNotification(`Stage "${movedStage.name}" moved ${direction}`, 'info');
  };

  const handleReviewTriggerToggle = (stageId, enabled) => {
    const currentTriggerCount = stages.filter(s => s.triggers_review && s.id !== stageId).length;
    
    if (enabled && currentTriggerCount >= 3) {
      addNotification('Maximum 3 stages can trigger review requests. Please disable another stage first.', 'warning');
      return;
    }
    
    setStages(prev => prev.map(stage => 
      stage.id === stageId 
        ? { ...stage, triggers_review: enabled }
        : stage
    ));

    const stageName = stages.find(s => s.id === stageId)?.name || 'Stage';
    addNotification(
      `Review trigger ${enabled ? 'enabled' : 'disabled'} for ${stageName}`, 
      'info'
    );
  };

  const handleSave = async () => {
    if (!currentOrganization?.id) {
      addNotification('No organization found. Please refresh the page and try again.', 'error');
      return;
    }

    // Validate that at least one stage exists
    if (stages.length === 0) {
      addNotification('At least one pipeline stage is required', 'error');
      return;
    }

    // Validate that all stages have names
    const hasEmptyNames = stages.some(stage => !stage.name.trim());
    if (hasEmptyNames) {
      addNotification('All stages must have a name', 'error');
      return;
    }
    
    try {
      setSaving(true);
      const result = await customerService.updatePipelineStages(stages, currentOrganization.id);
      onSave(stages);
      onClose();
      addNotification('Pipeline stages updated successfully!', 'success');
    } catch (error) {
      console.error('Error saving pipeline stages:', error);
      addNotification('Failed to save pipeline stages. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Reset to default stages? This will remove all custom stages.')) {
      const defaultStages = [
        { id: 'temp-1', name: 'Lead', color: '#3B82F6', icon: 'UserPlus', position: 1, is_active: true, triggers_review: false },
        { id: 'temp-2', name: 'Contacted', color: '#F59E0B', icon: 'Phone', position: 2, is_active: true, triggers_review: false },
        { id: 'temp-3', name: 'Qualified', color: '#8B5CF6', icon: 'CheckCircle', position: 3, is_active: true, triggers_review: false },
        { id: 'temp-4', name: 'Proposal Sent', color: '#F97316', icon: 'FileText', position: 4, is_active: true, triggers_review: false },
        { id: 'temp-5', name: 'Negotiation', color: '#EF4444', icon: 'MessageSquare', position: 5, is_active: true, triggers_review: false },
        { id: 'temp-6', name: 'Won', color: '#10B981', icon: 'Trophy', position: 6, is_active: true, triggers_review: true },
        { id: 'temp-7', name: 'Lost', color: '#6B7280', icon: 'XCircle', position: 7, is_active: true, triggers_review: false }
      ];
      setStages(defaultStages);
      addNotification('Pipeline stages reset to defaults.', 'info');
    }
  };

  const getColorPreview = (hexColor) => {
    const colorMap = {
      '#3B82F6': 'bg-blue-100 text-blue-800',
      '#10B981': 'bg-green-100 text-green-800',
      '#F59E0B': 'bg-yellow-100 text-yellow-800',
      '#EF4444': 'bg-red-100 text-red-800',
      '#8B5CF6': 'bg-purple-100 text-purple-800',
      '#F97316': 'bg-orange-100 text-orange-800',
      '#EC4899': 'bg-pink-100 text-pink-800',
      '#6366F1': 'bg-indigo-100 text-indigo-800',
      '#14B8A6': 'bg-teal-100 text-teal-800',
      '#6B7280': 'bg-gray-100 text-gray-800'
    };
    return colorMap[hexColor] || 'bg-gray-100 text-gray-800';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={onClose}
          aria-hidden="true"
        ></div>

        {/* This element is to trick the browser into centering the modal contents. */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Configure Pipeline Stages
                </h3>
                <p className="text-sm text-gray-600">
                  Customize your sales pipeline stages to match your business process
                </p>
              </div>
              <Button variant="ghost" onClick={onClose} disabled={saving}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-4 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading pipeline stages...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Stages List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Pipeline Stages</h4>
                    <Button variant="primary" size="sm" onClick={handleAddStage} disabled={saving}>
                      <Icon name="Plus" size={16} className="mr-2" />
                      Add Stage
                    </Button>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {stages.map((stage, index) => (
                      <div key={stage.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getColorPreview(stage.color)}`}>
                              <Icon name={stage.icon} size={14} className="mr-1" />
                              {stage.name}
                              {stage.triggers_review && (
                                <Icon name="Star" size={12} className="ml-1 text-yellow-500" title="Triggers review requests" />
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReviewTriggerToggle(stage.id, !stage.triggers_review)}
                              disabled={saving}
                              className={stage.triggers_review ? 'text-yellow-600 hover:bg-yellow-50' : 'text-gray-400 hover:bg-gray-100'}
                              title={stage.triggers_review ? 'Disable review trigger' : 'Enable review trigger'}
                            >
                              <Icon name="Star" size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveStage(stage.id, 'up')}
                              disabled={index === 0 || saving}
                            >
                              <Icon name="ChevronUp" size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMoveStage(stage.id, 'down')}
                              disabled={index === stages.length - 1 || saving}
                            >
                              <Icon name="ChevronDown" size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditStage(stage)}
                              disabled={saving}
                            >
                              <Icon name="Edit" size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteStage(stage)}
                              disabled={saving}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Icon name="Trash" size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Edit Form */}
                <div>
                  {editingStage ? (
                    <div className="bg-white border-2 border-blue-200 rounded-lg p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-900 text-lg">
                        {isAddingNew ? 'Add New Stage' : 'Edit Stage'}
                      </h4>
                        <div className="flex items-center space-x-2">
                          <Button variant="primary" onClick={handleSaveStage}>
                            <Icon name="Check" size={16} className="mr-2" />
                            {isAddingNew ? 'Add Stage' : 'Save Stage'}
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setEditingStage(null);
                              setIsAddingNew(false);
                            }}
                          >
                            <Icon name="X" size={16} className="mr-2" />
                            Cancel
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Stage Name *
                          </label>
                          <Input
                            value={editingStage.name}
                            onChange={(e) => setEditingStage(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter stage name"
                            className={!editingStage.name.trim() ? 'border-red-300 focus:border-red-500' : ''}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveStage();
                              }
                            }}
                          />
                          {!editingStage.name.trim() && (
                            <p className="mt-1 text-sm text-red-600">Stage name is required</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Color
                          </label>
                          <div className="grid grid-cols-5 gap-2">
                            {colorOptions.map((color) => (
                              <button
                                key={color.value}
                                onClick={() => setEditingStage(prev => ({ ...prev, color: color.value }))}
                                className={`w-8 h-8 rounded-full ${color.preview} border-2 ${
                                  editingStage.color === color.value ? 'border-blue-600' : 'border-transparent'
                                }`}
                                title={color.label}
                              />
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Icon
                          </label>
                          <div className="grid grid-cols-6 gap-2 max-h-32 overflow-y-auto">
                            {iconOptions.map((iconName) => (
                              <button
                                key={iconName}
                                onClick={() => setEditingStage(prev => ({ ...prev, icon: iconName }))}
                                className={`p-2 rounded border ${
                                  editingStage.icon === iconName 
                                    ? 'border-blue-600 bg-blue-50' 
                                    : 'border-gray-300 hover:bg-gray-100'
                                }`}
                              >
                                <Icon name={iconName} size={16} />
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Review Trigger
                          </label>
                          <div className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="triggers-review"
                              checked={editingStage.triggers_review || false}
                              onChange={(e) => {
                                const enabled = e.target.checked;
                                const currentTriggerCount = stages.filter(s => s.triggers_review && s.id !== editingStage.id).length;
                                
                                if (enabled && currentTriggerCount >= 3) {
                                  addNotification('Maximum 3 stages can trigger review requests. Please disable another stage first.', 'warning');
                                  return;
                                }
                                
                                setEditingStage(prev => ({ ...prev, triggers_review: enabled }));
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="triggers-review" className="text-sm text-gray-700">
                              Automatically send review requests when customers reach this stage
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Maximum 3 stages can trigger reviews. {stages.filter(s => s.triggers_review).length}/3 stages configured.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                      <Icon name="Settings" size={48} className="text-gray-400 mx-auto mb-4" />
                      <h4 className="font-medium text-gray-900 mb-2">Select a stage to edit</h4>
                      <p className="text-sm text-gray-600">
                        Click on a stage from the list to modify its properties
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <Button 
                variant="outline" 
                onClick={handleResetToDefaults}
                disabled={saving}
              >
                <Icon name="RotateCcw" size={16} className="mr-2" />
                Reset to Defaults
              </Button>
              
              <div className="flex items-center space-x-3">
                <Button variant="outline" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700 border-green-600"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving Pipeline...
                    </>
                  ) : (
                    <>
                      <Icon name="Save" size={16} className="mr-2" />
                      Apply All Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
            <div className="mt-2 text-center">
              <p className="text-xs text-gray-500">
                Individual stage changes are saved immediately. This button applies all changes to your pipeline.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StageConfigModal; 